import {
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import css from './Campo.module.css';

interface Moldura {
  rotulo: string;
  erro?: string | undefined;
  dica?: ReactNode;
}

/** Rótulo + controle + dica/erro, com ids e aria ligados (leitor de tela anuncia o erro). */
function MolduraCampo({
  rotulo,
  erro,
  dica,
  id,
  children,
}: Moldura & { id: string; children: ReactNode }) {
  return (
    <div className={css.campo}>
      <label className={css.rotulo} htmlFor={id}>
        {rotulo}
      </label>
      {children}
      {dica && !erro && (
        <span id={`${id}-dica`} className={css.dica}>
          {dica}
        </span>
      )}
      {erro && (
        <span id={`${id}-erro`} className={css.erro} role="alert">
          {erro}
        </span>
      )}
    </div>
  );
}

function ariaDo(id: string, { erro, dica }: Moldura) {
  return {
    'aria-invalid': erro ? true : undefined,
    'aria-describedby': erro ? `${id}-erro` : dica ? `${id}-dica` : undefined,
  } as const;
}

function classeDo(erro: string | undefined, extra?: string) {
  return [css.controle, erro && css.comErro, extra].filter(Boolean).join(' ');
}

export function Campo({
  rotulo,
  erro,
  dica,
  className,
  id: idExterno,
  ref,
  ...resto
}: Moldura & InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  const idGerado = useId();
  const id = idExterno ?? idGerado;
  return (
    <MolduraCampo rotulo={rotulo} erro={erro} dica={dica} id={id}>
      <input
        ref={ref}
        id={id}
        className={classeDo(erro, className)}
        {...ariaDo(id, { rotulo, erro, dica })}
        {...resto}
      />
    </MolduraCampo>
  );
}

export function CampoTexto({
  rotulo,
  erro,
  dica,
  className,
  ref,
  ...resto
}: Moldura & TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }) {
  const id = useId();
  return (
    <MolduraCampo rotulo={rotulo} erro={erro} dica={dica} id={id}>
      <textarea
        ref={ref}
        id={id}
        className={classeDo(erro, className)}
        {...ariaDo(id, { rotulo, erro, dica })}
        {...resto}
      />
    </MolduraCampo>
  );
}

export function CampoSelecao({
  rotulo,
  erro,
  dica,
  className,
  children,
  ...resto
}: Moldura & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <MolduraCampo rotulo={rotulo} erro={erro} dica={dica} id={id}>
      <select
        id={id}
        className={classeDo(erro, className)}
        {...ariaDo(id, { rotulo, erro, dica })}
        {...resto}
      >
        {children}
      </select>
    </MolduraCampo>
  );
}
